import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { TierBadge } from "@/components/waste/tier-badge";
import { EventLink } from "@/components/admin/event-link";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { cantVerifyReasonLabel } from "@/lib/cant-verify-reasons";

/**
 * The captures behind one Devices row's flag count (POS2-1123).
 *
 * The counts were the only trace a can't-verify left on any screen, so a run of 21 flags on
 * one camera could not be acted on — there was no way to see what the 21 were. Everything
 * shown here was already stored; nothing new is captured.
 *
 * Mounted only while open, which is what makes "fetched when the dialog opens" true —
 * `useQuery` has no idle state and the page's `useAdminQuery` takes no `enabled` flag, so the
 * request follows the mount rather than a condition.
 */
const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
const BASE = `${API_URL}/api/v1/admin/labelling`;

/** Mirrors `FLAGGED_CAPTURE_LIMIT` in `admin_labelling.py`, which is what the API bounds to.
 *
 * Quoted in the truncation line below rather than hardcoded in the sentence, so the number on
 * screen cannot drift from the number returned — the same reason `SHIFT_LOG_LIMIT` exists on
 * the labelling page. A silent cap on this list would read as "these are all of them", which
 * is the failure mode the list exists to fix.
 */
const FLAGGED_CAPTURE_LIMIT = 50;

export type FlaggedCapture = {
  waste_event_id: string;
  org_id: string;
  org_name: string;
  logged_at: string;
  flagged_at: string;
  reason: string;
  labeller_email: string;
  item_weight: number;
  classification_level: number | null;
  image_read_url: string | null;
};

type FlaggedCaptures = {
  captures: FlaggedCapture[];
  truncated: boolean;
};

/** The device row this dialog opens from — only the parts needed to address and title it. */
export type FlaggedCapturesTarget = {
  device_serial: string;
  device_name: string | null;
  site_id: string;
  site_name: string;
  flags: number;
};

function shortDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The capture, or a placeholder saying which way it is missing.
 *
 * Two distinct absences, because the dialog would otherwise lie about one of them:
 *
 * * **no key at all** — `waste_events.image_url` is NULL, so the photo never arrived. That is
 *   itself one of the reasons a labeller flags (`no_image`).
 * * **a key that will not fetch** — the R2 object is gone or unreadable. Without the `onError`
 *   this renders as a broken frame, and `image_wont_load` is *also* one of the reasons, so the
 *   capture most likely to fail here is precisely the one flagged for failing. Locally it is
 *   every capture, since the seed's `seed/labelling/*.jpg` keys have no objects behind them.
 *
 * The placeholder says "No photo" rather than "No image" because `no_image`'s own label is "No
 * image" — repeating it verbatim reads as the reason being printed twice.
 */
function CaptureThumbnail({ capture }: { capture: FlaggedCapture }) {
  const [failed, setFailed] = useState(false);
  const box =
    "flex h-20 w-20 shrink-0 items-center justify-center rounded bg-gray-50 text-center text-xs text-gray-400";

  if (!capture.image_read_url) return <div className={box}>No photo</div>;
  if (failed) return <div className={box}>Photo will not load</div>;
  return (
    <img
      src={capture.image_read_url}
      // Described rather than decorative: this capture is what the labeller could not judge,
      // which is the whole subject of the dialog. An empty alt would drop it out of the
      // accessibility tree entirely.
      alt={`Capture flagged as ${cantVerifyReasonLabel(capture.reason)}`}
      onError={() => setFailed(true)}
      className="h-20 w-20 shrink-0 rounded object-cover"
    />
  );
}

export function FlaggedCapturesDialog({
  target,
  period,
  onClose,
}: {
  target: FlaggedCapturesTarget;
  period: string;
  onClose: () => void;
}) {
  const headers = useApiHeaders();
  const query = useQuery<FlaggedCaptures>({
    queryKey: ["admin-labelling", "device-flags", target.device_serial, target.site_id, period],
    queryFn: async () => {
      const params = new URLSearchParams({ site_id: target.site_id, period });
      const res = await fetch(
        `${BASE}/devices/${encodeURIComponent(target.device_serial)}/flags?${params}`,
        { headers },
      );
      if (!res.ok) throw new Error(`Failed to load flagged captures: ${res.status}`);
      return (await res.json()).data as FlaggedCaptures;
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Flagged captures</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500">
          <span className="font-mono text-xs text-gray-700">{target.device_serial}</span>
          {target.device_name && ` · ${target.device_name}`} · {target.site_name}
        </p>

        {query.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {query.isError && (
          <p className="text-sm text-red-600">
            Could not load the captures for this device. The counts in the table are unaffected.
          </p>
        )}

        {query.data && query.data.captures.length === 0 && (
          <p className="text-sm text-gray-500">
            No flagged captures for this device in this period.
          </p>
        )}

        {query.data && query.data.captures.length > 0 && (
          <ul className="space-y-3" data-testid="flagged-captures">
            {query.data.captures.map((capture) => (
              <li
                key={`${capture.waste_event_id}-${capture.flagged_at}`}
                className="flex gap-3 rounded-md border border-black/[0.06] p-3"
              >
                {/* The reason a capture was flagged is often that there is no usable photo, so
                    the absent case is named rather than left as a broken frame. */}
                <CaptureThumbnail capture={capture} />
                <div className="min-w-0 flex-1 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700">{cantVerifyReasonLabel(capture.reason)}</span>
                    {/* Unconditional: `TierBadge` renders a null level as "Unanalysed", which is
                        the honest reading here — an event the classifier never resolved is a
                        large share of what gets flagged, and hiding the badge would make those
                        rows look like the tier was simply not shown. */}
                    <TierBadge level={capture.classification_level} />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {capture.item_weight.toLocaleString("en-GB", { maximumFractionDigits: 2 })} kg ·
                    flagged {shortDateTime(capture.flagged_at)} by {capture.labeller_email}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Captured {shortDateTime(capture.logged_at)} ·{" "}
                    <EventLink
                      eventId={capture.waste_event_id}
                      orgId={capture.org_id}
                      orgName={capture.org_name}
                    />
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {query.data?.truncated && (
          <p className="text-xs text-gray-500">
            Showing the {FLAGGED_CAPTURE_LIMIT} most recent of {target.flags} flags for this device
            in this period.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
